import { Component, ElementRef, inject } from "@angular/core";
import { AnalyticsService } from '@tytapp/analytics';
import { ApiCountry, ApiPoll, ApiPostPollsIdSign, ApiUser, CountriesApi, PetitionsApi, PollsApi } from "@tytapp/api";
import { BillingService } from '@tytapp/billing';
import { CanonicalURLService, OpenGraph, PageComponent } from "@tytapp/common";
import { environment } from "@tytapp/environment";
import { isClientSide } from '@tytapp/environment-utils';
import { WhatIsAMissionComponent } from '@tytapp/polls';
import { UserService } from "@tytapp/user";
import { TaskCompletedComponent } from './modals/task-completed/task-completed.component';

interface ShareaholicParams {
    v: number;
    apitype: number;
    apikey: string;
    service: string;
    title: string;
    link: string;
    notes: string;
    media_link: string;
}

@Component({
    templateUrl: './petition-page.component.html',
    styleUrls: ['./petition-page.component.scss']
})
export class PetitionPageComponent extends PageComponent {
    //#region Dependencies

    private pollsApi = inject(PollsApi);
    private opengraph = inject(OpenGraph);
    private userService = inject(UserService);
    private analytics = inject(AnalyticsService);
    private billing = inject(BillingService);
    private countriesApi = inject(CountriesApi);
    private element = inject(ElementRef);
    private petitionApi = inject(PetitionsApi);
    private canonicalService = inject(CanonicalURLService)

    //#endregion
    //#region Lifecycle

    async init() {
        this.route.paramMap.subscribe(async params => {
            await this.userService.ready;

            this.enableComments = await this.shell.hasFeature('apps.web.enable_petition_comments');
            this.pageTitle = 'Petition';

            this.slug = params.get('id');

            let petition: ApiPoll;

            try {
                petition = await this.pollsApi.getPoll(this.slug).toPromise();
            } catch (e) {
                if (e.status === 404 || e.error === 'not-found') {
                    this.redirection.go(`/petitions`, true);
                    return;
                }

                throw e;
            }

            if (petition.type !== 'Petition') {
                this.redirection.go(`/polls/${this.slug}`, true);
                return;
            }

            this.petition = petition;
            this.signed = this.reallySigned = !!this.userVote;
            if (this.amplify) {
                this.assumeSigned();
            }

            this.updateOffers();

            if (!this.petition) {
                this.router.navigateByUrl('/petitions');
                return;
            }

            this.pageTitle = this.petition.title;

            this.updateSocialMetadata();

            if (this.canonicalService.redirectToCanonical(`/campaigns/${this.petition.slug || this.petition.id}`)) {
                return;
            }

            this.opengraph.setMetadata({
                title: this.socialShareTitle,
                image: this.socialShareImage || `${environment.urls.webRoot}/assets/polls/flag-bg.png`,
                description: this.socialShareDescription
            });

            this.socialShareBoxImage = this.socialShareImage || `${environment.urls.webRoot}/assets/polls/flag-bg.png`;

            await this.addPlayerMetadata();

            if (!this.user) {
                // Check the local storage flag after setting the slug
                const signedFlag = localStorage.getItem(`petition_signed_${this.slug}`);
                if (signedFlag) {
                    this.signed = true;
                    this.reallySigned = true;
                }
            }
        });

        this.subscribe(this.billing.entitlementChanged, entitled => this.entitled = entitled);
        this.subscribe(this.userService.userChanged, user => {
            this.user = user;
        });

        this.subscribe(this.countriesApi.all(), countries => {
            this.countriesList = countries;

            // Put US at the top
            let usIndex = this.countriesList.findIndex(x => x.alpha2 == 'US');
            let us = this.countriesList[usIndex];
            if (us) {
                this.countriesList.splice(usIndex, 1);
                this.countriesList.unshift(us);
            }
        });

        if (isClientSide()) {
            window.addEventListener('message', (event) => {
                if (event.origin !== environment.urls.accounts) return;

                if (event.data && event.data.event === 'noThanks') {
                    this.dismissedOffers = true;
                    this.updateOffers();
                }
            });
        }

        this.route.data.subscribe(data => {
            this.amplify = data.amplify ?? false;
        });
    }

    destroy() {
        if (this.shareSub) {
            this.shareSub.unsubscribe();
            this.shareSub = null;
        }
    }

    //#endregion
    //#region Template Properties

    state: 'loading' | 'ready' = 'loading';
    slug: string;
    enableComments = true;
    petition: ApiPoll;
    signed = false;
    reallySigned = false;
    socialShareTitle: string;
    socialShareDescription: string;
    socialShareImage: string;
    socialShareBoxImage: string;
    user: ApiUser;
    entitled = false;
    visitorEmail: string = '';
    visitorFirstName: string = '';
    visitorLastName: string = '';
    visitorCountry: string = null;
    visitorErrorMessage: string = '';
    showVisitorExtraFields: boolean = false;
    recaptchaToken: string = '';
    isSigning: boolean = false;
    countriesList: ApiCountry[] = [];
    showEmbeddedOffers = false;
    showEmbeddedSignUp = false;
    dismissedOffers = false;
    collapsedMoreInfo = false;
    showOffersWithoutVote = false;
    shareSub: { unsubscribe: () => void };

    //#endregion
    //#region Properties

    get commentIdentifier() {
        return `poll_${this.slug}`;
    }

    get closedEarlier() {
        if (this.petition && this.petition.closes_at) {
            if (new Date(this.petition.closes_at).getTime() < Date.now())
                return true;
        }

        return false;
    }

    get actionText() {
        if (this.closedEarlier)
            return 'Closed';
        else if (!this.signed)
            return 'Sign Now';
        else
            return 'Signed';
    }

    get totalVotes() {
        if (!this.petition)
            return 0;

        let voteCount = 0;

        if (!this.petition.questions)
            return 0;

        for (let question of this.petition.questions) {
            voteCount = Math.max(voteCount, question.total_votes);
        }

        return voteCount;
    }

    get remainingSignaturesToGoal() {
        let remaining: number = 0;

        if (this.petition && this.petition.goal) {
            if (this.totalVotes < this.petition.goal.number) {
                remaining = this.petition.goal.number - this.totalVotes;
            }
        }

        return remaining;
    }

    get userVote() {
        if (!this.petition || !this.petition.questions || this.petition.questions.length === 0)
            return null;

        return this.petition.questions[0].vote ? this.petition.questions[0].vote : null;
    }

    get petitionCaption(): string {
        let caption: string = "";

        if (this.petition && this.petition.questions && this.petition.questions[0] && this.petition.questions[0].body) {
            caption = this.petition.questions[0].body;
        }

        return caption;
    }

    get progressText() {
        if (!this.petition.active_mission || !this.petition.active_mission_progress)
            return undefined;
        const completed = this.petition.active_mission_progress.completed_share_targets.length;
        const totalMissions = this.petition.active_mission.share_targets.length;
        return `${completed} out of ${totalMissions} Mission${totalMissions > 1 ? 's' : ''} tasks completed`;
    }

    get missionProgress() {
        if (!this.petition.active_mission_progress || !this.petition.active_mission)
            return 0;

        const completed = this.petition.active_mission_progress.completed_share_targets.length;
        const totalMissions = this.petition.active_mission.share_targets.length;
        if (completed === 0) return 0;
        return completed / totalMissions;
    }

    get isMissionCompleted() {
        if (!this.petition.active_mission_progress)
            return false;
        return this.petition.active_mission_progress.completed_share_targets.length === this.petition.active_mission.share_targets.length;
    }

    get shouldShowMissionProgress() {
        return this.petition.active_mission;
    }

    get petitionMissionReachCount() {
        return this.petition.active_mission?.reach_count ?? 0;
    }

    //#endregion
    //#region Logic

    shareTargetEnabled(name: string) {
        if (!this.petition.active_mission)
            return true;
        return this.petition.active_mission.share_targets.includes(name);
    }

    expandForm() {
        if (this.showVisitorExtraFields)
            return;

        this.showVisitorExtraFields = true;
        this.visitorErrorMessage = null;
    }

    amplify = false;

    async addPlayerMetadata() {
        if (this?.petition?.active_mission) {
            this.opengraph.setTwitterPlayerMetadata({
                p: this.petition.id,
                m: `${ this.petition.active_mission.id }`
            });
        }
    }

    async signPetition() {

        if (this.isSigning) {
            return;
        }

        this.visitorErrorMessage = null;

        if (this.signed) {
            return;
        }

        if (!this.user) {
            if (!this.visitorEmail) {
                this.visitorErrorMessage = "Please enter your Email address";
                return;
            }

            if (!this.visitorFirstName) {
                this.visitorErrorMessage = "Please enter your First Name";
                return;
            }

            const emailRegexValidation = new RegExp(/^\S+@\S+\.\S+$/);
            if (!emailRegexValidation.test(this.visitorEmail)) {
                this.visitorErrorMessage = "Please enter a valid Email address";
                return;
            }
        }

        let result: ApiPoll | void;

        this.isSigning = true;

        const params: ApiPostPollsIdSign = {};
        if (!this.user) {
            Object.assign(params, <ApiPostPollsIdSign>{
                email: this.visitorEmail,
                first_name: this.visitorFirstName,
                last_name: this.visitorLastName,
                country: this.visitorCountry,
                recaptcha_token: this.recaptchaToken
            });
        }

        let success = false;
        try {
            result = await this.pollsApi.signPetition(this.petition.slug, params).toPromise();
            success = true;
        } catch (error) {
            if (error && error.json)
                error = error.json();

            let message = error.message || error.error
                || "There was an error when signing the petition, please try again later";

            if (message !== 'already-signed') {
                this.visitorErrorMessage = message;
                this.isSigning = false;
                return;
            }
        }

        // Record beacons
        if (success) {
            localStorage.setItem(`petition_signed_${this.petition.slug}`, 'true');
            await this.sendBeacons();
        }

        if (result)
            this.petition = result;

        this.signed = true;
        this.reallySigned = true;
        this.isSigning = false;

        this.updateOffers();
        this.updateSocialMetadata();

        setTimeout(() => {
            let el: HTMLElement = this.element.nativeElement;
            let socialShareBox = el.querySelector('.share');
            if (socialShareBox) {
                socialShareBox.scrollIntoView({ behavior: "smooth", block: "center" });
            }
        }, 100);
    }

    async sendBeaconsWhileTesting() {
        let forceBeaconsPrev = window['force_beacons'];
        try {
            window['force_beacons'] = 1; // send even in preproduction
            await this.sendBeacons();
        } finally {
            window['force_beacons'] = forceBeaconsPrev;
        }

        alert(`Beacons have been sent to Facebook and Google Analytics`);
    }

    async sendBeacons() {
        await this.analytics.sendBeacons('Sign Petition', async () => {
            await Promise.all([
                this.analytics.sendBeaconGA4(`signed_petition`, {
                    id: this.petition.id,
                    slug: this.petition.slug,
                    title: this.petition.title,
                    url: `https://${environment.urls.webRoot}/campaigns/${this.petition.slug}`
                }),
                this.analytics.sendBeaconFB(
                    'SignPetition',
                    this.petition.slug,
                    {
                        title: this.petition.title,
                        url: `https://${environment.urls.webRoot}/campaigns/${this.petition.slug}`
                    },
                    true
                )
            ]);
        });
    }

    setShowOffersWithoutVote() {
        this.showOffersWithoutVote = true;
        this.updateOffers();
    }

    devAssumeSigned() {
        this.assumeSigned();
        this.reallySigned = true;
    }

    assumeSigned() {
        this.signed = true;
    }

    updateOffers() {
        this.showEmbeddedOffers = false;
        this.showEmbeddedSignUp = false;

        if (this.signed || this.showOffersWithoutVote) {
            if (this.petition?.offer_set && (!this.user || !this.entitled) && !this.dismissedOffers) {
                this.showEmbeddedOffers = true;
            } else if (!this.user) {
                this.showEmbeddedSignUp = true;
            }
        }
    }

    updateSocialMetadata() {
        if (!this.petition)
            return;

        // Determine the user's first name

        let firstNameForSocial = this.visitorFirstName;
        if (this.signed && this.user && this.user.profile)
            firstNameForSocial = this.user.profile.first_name;

        this.socialShareDescription = '';
        this.socialShareTitle = '';

        // Personalize title

        if (firstNameForSocial)
            this.socialShareTitle = `${firstNameForSocial} Needs Your Support - `;

        // Add dynamic information about progress

        if (this.petition.goal && this.petition.goal.number) {
            if (this.remainingSignaturesToGoal > 0) {
                this.socialShareDescription = `${(this.petition.goal.number - this.totalVotes).toLocaleString('en-US')} more signatures are needed to reach our goal. `;
            } else if (this.totalVotes > 0) {
                this.socialShareDescription = `${this.totalVotes.toLocaleString('en-US')} signatures so far! `;
            }
        }

        // Add base metadata

        this.socialShareTitle += this.petition.title;
        this.socialShareDescription += this.petition.description;
        this.socialShareImage = this.petition.image;

        if (this.petition.active_mission) {
            this.socialShareImage = this.petition.active_mission.image_url;
        }

    }

    get recaptchaSiteKey() {
        return environment.recaptchaV2SiteKey;
    }

    captchaSolved(response: string) {
        this.recaptchaToken = response;
    }

    async onShareCompleted(event: ShareaholicParams) {
        if (!this.petition.active_mission)
            return;

        if (this.user) {
            try {
                await this.petitionApi.recordMissionShare(
                    this.petition.id,
                    `${this.petition.active_mission.id}`, { share_target: event.service }
                ).toPromise();
            } catch (e) {
                this.logger.error(`Failed to record mission share: ${e.message}`);
                this.logger.error(e);
            }
        }

        if (!this.petition.active_mission_progress.completed_share_targets.includes(event.service)) {
            this.petition.active_mission_progress.completed_share_targets.push(event.service);
            this.shell.showDialog(TaskCompletedComponent, this.progressText, this.missionProgress);
        }
    }

    petitionHasMission(): boolean {
        return !!this.petition.active_mission;
    }

    setAddthisInfo(provider: string): ShareaholicParams {
        return {
            v: 1,
            apitype: 1, //1=Redirect, 2=Pingback, 3=1x1 pixel
            apikey: '8943b7fd64cd8b1770ff5affa9a9437b',
            service: provider,
            title: (this.petition.active_mission ? this.petition.active_mission.post_copy : this.petition.title) ?? 'Sign now',
            link: `${environment.urls.webRoot}/campaigns/${this.petition.slug}${this.petition.active_mission ? `?m=${this.petition.active_mission.id}` : ``}`,
            notes: this.petition.description,
            media_link: this.petition.image,
        };
    }

    sharePetition(provider: 'facebook' | 'twitter' | 'reddit') {
        const url = new URL('https://www.shareaholic.com/api/share/');
        const params = this.setAddthisInfo(provider);
        Object.entries(params).forEach(([name, value]) => {
            url.searchParams.set(name, value);
        });
        window.open(url, params.title, 'width=300, height=300');
        this.onShareCompleted(params);
    }

    get shouldShowReachBox() {
        if (!this.petition.active_mission)
            return false;

        if (!this.petition.active_mission.brandwatch_query_id && !this.petition.active_mission.reach_count)
            return false;

        return true;
    }

    checkProviderIsCompleted(provider: string) {
        return this.petition.active_mission_progress.completed_share_targets.includes(provider);
    }

    openHelpModal() {
        this.shell.showDialog(WhatIsAMissionComponent, !!this.user);
    }

    redirectToSignUp() {
        this.userService.source = 'free-signup/embedded';
        this.router.navigateByUrl('/signup');
    }
}
